/**
 * Dashboard de dirección — SUPERADMIN (BHM-CLD-REQ-028).
 * Datos reales de /work/overview: tareas propias y del equipo, retrasadas, bloqueadas,
 * carga por empleado, proyectos con incidencias y tiempo registrado. Sin KPIs ficticios.
 * Más «Equipo en ejecución» (REQ-045a): estado de jornada, tarea actual/última y siguiente por empleado.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { isSuperAdmin } from '../utils/permissions';
import { workApi, errMsg, fmtDate, type Overview, type OverviewTask } from '../stores/workApi';
import { Icon, ErrorBanner, PriorityLabel, StatusPill } from '../components/work/WorkUi';
import TeamLivePanel from '../components/work/TeamLivePanel';

function Kpi({ icon, label, value, to, warn = false }: { icon: string; label: string; value: string | number; to?: string; warn?: boolean }) {
  const body = (
    <div className={`rounded-xl border p-4 ${warn ? 'border-[#F59E0B]/40 bg-[#F59E0B]/5' : 'border-outline-variant bg-surface-container-low'}`}>
      <p className="flex items-center gap-2 text-xs text-on-surface-variant"><Icon name={icon} className={`text-base ${warn ? 'text-[#F59E0B]' : ''}`} />{label}</p>
      <p className="mt-1 text-2xl font-semibold text-on-surface">{value}</p>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function TaskList({ title, icon, rows, empty }: { title: string; icon: string; rows: OverviewTask[]; empty: string }) {
  return (
    <section className="rounded-xl border border-outline-variant bg-surface-container-low p-5">
      <h2 className="mb-3 flex items-center gap-2 font-title-md text-on-surface"><Icon name={icon} className="text-on-surface-variant" />{title} · {rows.length}</h2>
      {rows.length === 0 ? <p className="py-3 text-sm text-on-surface-variant/70">{empty}</p> : (
        <ul className="divide-y divide-outline-variant/60">
          {rows.slice(0, 8).map((t) => (
            <li key={t.id} className="flex items-center gap-3 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate text-on-surface">{t.title}</p>
                <p className="truncate text-xs text-on-surface-variant"><Link to={`/proyectos/${t.projectId}`} className="hover:text-on-surface">{t.projectName}</Link> · {t.assignee ?? 'Sin asignar'}</p>
              </div>
              <PriorityLabel priority={t.priority} />
              <span className="text-xs text-on-surface-variant">{fmtDate(t.dueDate)}</span>
              <StatusPill status={t.status} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function DirectorDashboard() {
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { workApi.overview().then(setData).catch((e) => setError(errMsg(e, 'No se pudo cargar el resumen'))); }, []);

  return (
    <div className="mx-auto max-w-[1600px] space-y-6 p-6">
      <div>
        <h1 className="font-headline-lg text-headline-lg text-on-surface">Dashboard de dirección</h1>
        <p className="mt-1 text-body-md text-on-surface-variant">Visión global de BarmetMedia · {user?.displayName || 'Usuario'}</p>
      </div>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {!data && !error && <p className="text-sm text-on-surface-variant">Cargando…</p>}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Kpi icon="folder_open" label="Proyectos abiertos" value={data.projects} to="/proyectos" />
            <Kpi icon="person" label="Mis tareas abiertas" value={data.myOpenTasks} to="/mi-trabajo" />
            <Kpi icon="groups" label="Tareas del equipo" value={data.teamOpenTasks ?? '—'} />
            <Kpi icon="event_busy" label="Retrasadas" value={data.overdue.length} warn={data.overdue.length > 0} />
            <Kpi icon="block" label="Bloqueadas" value={data.blocked.length} warn={data.blocked.length > 0} />
            <Kpi icon="schedule" label={`Horas desde ${fmtDate(data.weekStart)}`} value={data.hoursThisWeek.toFixed(1)} to="/operaciones/revision-tiempos" />
            {data.pendingApproval && <Kpi icon="fact_check" label="Horas pendientes de aprobar" value={`${data.pendingApproval.hours.toFixed(1)} (${data.pendingApproval.entries})`} warn={data.pendingApproval.entries > 0} to="/operaciones/revision-tiempos" />}
          </div>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            {isSuperAdmin(user?.role ?? '') && <TeamLivePanel />}
            <TaskList title="Retrasadas" icon="event_busy" rows={data.overdue} empty="Sin tareas retrasadas" />
            <TaskList title="Bloqueadas" icon="block" rows={data.blocked} empty="Sin tareas bloqueadas" />
            <section className="rounded-xl border border-outline-variant bg-surface-container-low p-5">
              <h2 className="mb-3 flex items-center gap-2 font-title-md text-on-surface"><Icon name="groups" className="text-on-surface-variant" />Carga por empleado</h2>
              {data.loadByEmployee.length === 0 ? <p className="py-3 text-sm text-on-surface-variant/70">Sin tareas asignadas ni tiempo registrado esta semana</p> : (
                <table className="w-full text-sm">
                  <thead className="text-left text-[11px] uppercase tracking-wider text-on-surface-variant"><tr><th className="py-1">Empleado</th><th className="py-1 text-right">Tareas abiertas</th><th className="py-1 text-right">Horas semana</th></tr></thead>
                  <tbody>{data.loadByEmployee.map((l) => <tr key={l.userId} className="border-t border-outline-variant/60"><td className="py-2 text-on-surface">{l.name}</td><td className="py-2 text-right">{l.openTasks}</td><td className="py-2 text-right">{l.hoursWeek.toFixed(1)}</td></tr>)}</tbody>
                </table>
              )}
            </section>
            {data.hoursByProjectWeek && (
              <section className="rounded-xl border border-outline-variant bg-surface-container-low p-5">
                <h2 className="mb-3 flex items-center gap-2 font-title-md text-on-surface"><Icon name="schedule" className="text-on-surface-variant" />Horas por proyecto (semana)</h2>
                {data.hoursByProjectWeek.length === 0 ? <p className="py-3 text-sm text-on-surface-variant/70">Sin tiempo registrado esta semana</p> : (
                  <ul className="divide-y divide-outline-variant/60">
                    {data.hoursByProjectWeek.map((p) => (
                      <li key={p.projectId} className="flex items-center justify-between py-2 text-sm">
                        <Link to={`/proyectos/${p.projectId}`} className="text-on-surface hover:text-[#FF5E1E]">{p.name}</Link>
                        <span className="text-on-surface-variant">{p.hours.toFixed(1)} h</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
            <section className="rounded-xl border border-outline-variant bg-surface-container-low p-5">
              <h2 className="mb-3 flex items-center gap-2 font-title-md text-on-surface"><Icon name="warning" className="text-on-surface-variant" />Proyectos con incidencias</h2>
              {data.projectsWithIncidents.length === 0 ? <p className="py-3 text-sm text-on-surface-variant/70">Sin incidencias</p> : (
                <ul className="divide-y divide-outline-variant/60">
                  {data.projectsWithIncidents.map((p) => (
                    <li key={p.projectId} className="flex items-center justify-between py-2 text-sm">
                      <Link to={`/proyectos/${p.projectId}`} className="text-on-surface hover:text-[#FF5E1E]">{p.name}</Link>
                      <span className="text-xs text-on-surface-variant">{p.overdue} retrasadas · {p.blocked} bloqueadas</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
